import { useState } from 'react';
import { Head, Link, router, usePage } from '@inertiajs/react';
import { EditorRenderer } from '@/components/Editor';

/**
 * Published Q&A data from the backend.
 */
interface PublishedQuestion {
    id: number;
    question: string;
    answer: string | null;
    user: { name: string } | null;
    answerer: { name: string } | null;
    answered_at: string | null;
}

/**
 * User's pending question.
 */
interface MyQuestion {
    id: number;
    question: string;
    is_answered: boolean;
    created_at: string;
}

/**
 * Pagination link from Laravel.
 */
interface PaginationLink {
    url: string | null;
    label: string;
    active: boolean;
}

/**
 * Paginated questions response.
 */
interface PaginatedQuestions {
    data: PublishedQuestion[];
    links: PaginationLink[];
    last_page: number;
}

/**
 * Props for the Ask page.
 */
interface Props {
    questions: PaginatedQuestions;
    myQuestions: MyQuestion[];
    isGm: boolean;
}

/**
 * Flash messages from the page props.
 */
interface PageProps {
    flash?: {
        success?: string;
        error?: string;
    };
}

/**
 * Public Ask the Feywild Times page - Q&A section.
 * Users can view published Q&A and submit their own questions.
 * Uses traditional newspaper-style typography with white background and black text.
 */
export default function Ask({ questions, myQuestions, isGm }: Props) {
    const { flash } = usePage<PageProps>().props;
    const [newQuestion, setNewQuestion] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    /**
     * Handle question submission.
     */
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrors({});

        router.post('/newspaper/ask', { question: newQuestion }, {
            onError: (errs) => {
                setErrors(errs);
                setIsSubmitting(false);
            },
            onSuccess: () => {
                setNewQuestion('');
                setIsSubmitting(false);
            },
        });
    };

    return (
        <>
            <Head title="Ask the Feywild Times" />

            <div className="min-h-screen bg-gray-50">
                {/* Newspaper Header */}
                <header className="bg-white border-b-4 border-double border-gray-900 py-4">
                    <div className="max-w-4xl mx-auto px-4 text-center">
                        <Link href="/newspaper" className="hover:opacity-80 transition-opacity">
                            <h1 className="text-3xl md:text-4xl font-serif font-bold text-black tracking-tight">
                                The Feywild Times
                            </h1>
                        </Link>
                        <p className="text-gray-600 font-serif italic mt-1">
                            Ask the Feywild Times
                        </p>
                    </div>
                </header>

                {/* Main Content */}
                <main className="max-w-4xl mx-auto px-4 py-6">
                    {/* Flash Messages */}
                    {flash?.success && (
                        <div className="mb-6 px-4 py-3 bg-green-100 border border-green-400 rounded text-green-800">
                            {flash.success}
                        </div>
                    )}

                    {/* Submit Question Form */}
                    <div className="bg-white border border-gray-300 rounded-lg p-4 mb-6">
                        <h2 className="text-xl font-serif font-bold text-black mb-3">
                            Have a Question?
                        </h2>
                        <p className="text-gray-700 mb-3">
                            Submit your question and our editors may answer it in a future edition!
                        </p>
                        <form onSubmit={handleSubmit}>
                            <textarea
                                value={newQuestion}
                                onChange={(e) => setNewQuestion(e.target.value)}
                                placeholder="What would you like to ask the Feywild Times?"
                                className="w-full px-4 py-3 border border-gray-300 rounded bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                                rows={3}
                                minLength={10}
                                maxLength={1000}
                                required
                            />
                            {errors.question && (
                                <p className="mt-1 text-sm text-red-600">{errors.question}</p>
                            )}
                            <div className="mt-3 flex justify-end">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-6 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 transition-colors font-serif disabled:opacity-50"
                                >
                                    {isSubmitting ? 'Submitting...' : 'Submit Question'}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* My Pending Questions */}
                    {myQuestions.length > 0 && (
                        <div className="bg-white border border-gray-300 rounded-lg p-4 mb-6">
                            <h2 className="text-lg font-serif font-bold text-black mb-3">
                                Your Pending Questions
                            </h2>
                            <div className="space-y-3">
                                {myQuestions.map((q) => (
                                    <div key={q.id} className="text-sm border-b border-gray-200 pb-3 last:border-0">
                                        <p className="text-gray-900 italic">"{q.question}"</p>
                                        <p className="text-gray-600 text-xs mt-1">
                                            Submitted {new Date(q.created_at).toLocaleDateString()}
                                            {q.is_answered ? ' • Answered (awaiting publication)' : ' • Awaiting answer'}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Published Q&A */}
                    <div>
                        <h2 className="text-2xl font-serif font-bold text-black mb-4 border-b border-gray-300 pb-2">
                            Published Questions & Answers
                        </h2>

                        {questions.data.length === 0 ? (
                            <div className="text-center py-12 text-gray-700">
                                <p>No questions have been answered yet. Be the first to ask!</p>
                            </div>
                        ) : (
                            <>
                                {/* Table of Contents */}
                                <div className="bg-white border border-gray-300 rounded-lg p-4 mb-6">
                                    <h3 className="text-lg font-serif font-bold text-black mb-3">
                                        Table of Contents
                                    </h3>
                                    <ol className="list-decimal list-inside space-y-2">
                                        {questions.data.map((q) => (
                                            <li key={`toc-${q.id}`} className="text-sm">
                                                <a
                                                    href={`#question-${q.id}`}
                                                    className="text-gray-700 hover:text-gray-500 hover:underline transition-colors"
                                                >
                                                    {q.question.length > 80
                                                        ? q.question.substring(0, 80) + '...'
                                                        : q.question}
                                                </a>
                                            </li>
                                        ))}
                                    </ol>
                                </div>

                                {/* Q&A Cards */}
                                <div className="space-y-4">
                                    {questions.data.map((q) => (
                                        <div
                                            key={q.id}
                                            id={`question-${q.id}`}
                                            className="bg-white border border-gray-300 rounded-lg p-4 scroll-mt-4"
                                        >
                                            <p className="text-lg font-serif font-medium text-gray-900 italic mb-3">
                                                "{q.question}"
                                            </p>
                                            {q.user && (
                                                <p className="text-xs text-gray-600 mb-3">
                                                    — Asked by {q.user.name}
                                                </p>
                                            )}
                                            {q.answer && (
                                                <div className="border-t border-gray-200 pt-3">
                                                    <div className="prose prose-sm max-w-none text-black">
                                                        <EditorRenderer content={q.answer} theme="light" />
                                                    </div>
                                                    {q.answerer && (
                                                        <p className="text-xs text-gray-900 mt-3">
                                                            {q.answered_at && (
                                                                <> Answered on {new Date(q.answered_at).toLocaleDateString()}</>
                                                            )}
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}

                        {/* Pagination */}
                        {questions.last_page > 1 && (
                            <div className="mt-6 flex justify-center gap-1">
                                {questions.links.map((link, index) => (
                                    <a
                                        key={index}
                                        href={link.url || '#'}
                                        className={`px-3 py-1 rounded text-sm font-serif ${
                                            link.active
                                                ? 'bg-gray-800 text-white'
                                                : link.url
                                                ? 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        }`}
                                        dangerouslySetInnerHTML={{ __html: link.label }}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Back to Newspaper */}
                    <div className="mt-6 text-center">
                        <Link
                            href="/newspaper"
                            className="text-gray-700 hover:text-gray-500 underline font-serif"
                        >
                            ← Back to The Feywild Times
                        </Link>
                    </div>
                </main>
            </div>
        </>
    );
}

